import { useEffect, useState, useRef, useCallback } from 'react';
import { useToast } from '../hooks/useToast';
import { useConfirm } from '../hooks/useConfirm';
import QRCode from 'qrcode';
import { useParams, useNavigate } from 'react-router-dom';
import { useI18n, formatDay } from '../i18n';
import {
  updateTournament, getTeams, getAgeGroups,
  uploadTournamentLogo, deleteTournamentLogo, deleteTournament, getWeightClasses,
} from '../api/client';
import { useAfterWrite } from '../hooks/useAfterWrite';
import { useLoad } from '../hooks/useLoad';
import { useAuthStore } from '../store/auth';
import { useTournamentData } from '../hooks/useTournamentData';
import { usePageTitle } from '../hooks/usePageTitle';
import { useUrlTab } from '../hooks/useUrlParam';
import { useBusy } from '../hooks/useBusy';
import { logBackgroundError } from '../observability';
import { PencilIcon, TrophyIcon, CameraIcon, LockIcon, LockOpenIcon, PlayIcon, CheckIcon } from '../components/shared/Icons';
import Skeleton from '../components/shared/Skeleton';
import LoadFailure from '../components/shared/LoadFailure';
import BackLink from '../components/shared/BackLink';
import TabBar from '../components/shared/TabBar';
import { useDeferredLoading } from '../hooks/useDeferredLoading';
import BulkRegisterModal from '../components/tournament/BulkRegisterModal';
import RegistrationPanel from '../components/tournament/RegistrationPanel';
import GuestPrompt from '../components/shared/GuestPrompt';
import TournamentFormModal from '../components/tournament/TournamentFormModal';
import CheckinTab from '../components/tournament/CheckinTab';
import WeighinTab from '../components/tournament/WeighinTab';
import TeamRatingsTab from '../components/tournament/TeamRatingsTab';
import OpenRingMatchPreviewModal from '../components/tournament/OpenRingMatchPreviewModal';
import BracketPreviewModal from '../components/tournament/BracketPreviewModal';
import ManualBoutAssignModal from '../components/tournament/ManualBoutAssignModal';
import BoutPlanModal from '../components/tournament/BoutPlanModal';
import BoutPivotAnalysis from '../components/tournament/BoutPivotAnalysis';
import PlanOfficialsModal from '../components/tournament/PlanOfficialsModal';
import ViewOfficialsModal from '../components/tournament/ViewOfficialsModal';
import OverviewTab from './tournament/OverviewTab';
import RegistrationsTab from './tournament/RegistrationsTab';
import OfficialsTab from './tournament/OfficialsTab';
import BoutsTab from './tournament/BoutsTab';
import BracketsTab from './tournament/BracketsTab';
import type { BracketPreview } from '../components/tournament/BracketPreviewModal';
import type { ManualBoutRow } from '../components/tournament/ManualBoutAssignModal';
import type { OpenRingPreviewData } from '../components/tournament/OpenRingMatchPreviewModal';
import AgeGroupPills from '../components/tournament/AgeGroupPills';
import { TournamentTypeBadge, TournamentInfoPills } from '../components/tournament/TournamentPills';
import TournamentDates, { tournamentDatesLine } from '../components/tournament/TournamentDates';
import TournamentBanner from '../components/tournament/TournamentBanner';
import CountryDisplay from '../components/shared/CountryDisplay';
import { runsAsOpenRing, hasChampionshipPart } from '../utils/tournamentType';
import { weighinStatusOn } from '../utils/tournament';
import { boutScoreState, canDeleteTournament, coachEditsLocked, registrationLocked } from '../utils/tournamentState';
import { successText } from '../i18n/labels';
import type {
  Team, Tournament, AgeGroup, WeightClass, Ring, BoutPlanView,
} from '../types';

const TAB_IDS = ['overview', 'registrations', 'officials', 'bouts', 'brackets', 'ratings', 'checkin', 'weighin'] as const;
type Tab = typeof TAB_IDS[number];

// #615 — on paper the tournament name is one line. A character of the bold
// name is about 0.6 em wide, so the font size is about 160cqi divided by the
// number of characters (the column is the container), from 14 px to 24 px.
// Tailwind needs whole class names, so the length picks a step, and each
// step fits its longest name. A longer name ends with an ellipsis.
const PRINT_NAME_SIZES: [number, string][] = [
  [30, 'print:text-[length:clamp(0.875rem,5.3cqi,1.5rem)]'],
  [36, 'print:text-[length:clamp(0.875rem,4.4cqi,1.5rem)]'],
  [44, 'print:text-[length:clamp(0.875rem,3.6cqi,1.5rem)]'],
  [54, 'print:text-[length:clamp(0.875rem,2.9cqi,1.5rem)]'],
];
const printNameSize = (chars: number) =>
  PRINT_NAME_SIZES.find(([max]) => chars <= max)?.[1] ?? 'print:text-[length:clamp(0.875rem,2.4cqi,1.5rem)]';

export default function TournamentDetail() {
  const { showError } = useToast();
  const { confirm } = useConfirm();
  const afterWrite = useAfterWrite();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { hasRole, user } = useAuthStore();
  const { t } = useI18n();

  // The DOOR onto athlete data, not a role (#406): two of the five are not
  // coaches. Same name and same role set as the backend middleware. The
  // SCOPE — whose teams — is `canRegisterAnyTeam` below.
  const canManageAthletes = hasRole('admin', 'tournament_manager', 'team_manager', 'head_coach', 'coach');
  // `jury` is in the set, so this was never "is a referee" (#406). It is
  // the capability: may this person take a bout official's seat?
  const canOfficiate = hasRole('referee', 'admin', 'jury');
  const isJury    = hasRole('jury', 'admin');

  // ── Shared tournament data (hook) ──────────────────────────��───────────────
  const data = useTournamentData(id);
  const {
    tournament, patchTournament, refreshTournament,
    bouts, patchBouts,
    registrations, patchRegistrations,
    referees,
    juryAssignments,
    managers,
    brackets, loadBrackets, bracketsVersion,
    boutSubs, setBoutSub,
    weighins, setWeighins,
    weighinEndedDays, setWeighinEnded,
    weighinProposal, setWeighinProposal,
    loading, reload, loadError, retryLoad,
    managesThisTournament, rings, tournamentDays, isMultiDay,
  } = data;

  // #404 — whose teams may this person register? The tournament's own TM and
  // an admin cover the whole event; `team_manager` is a federation-wide roster
  // authority and registers any team. Everyone else gets the teams they coach.
  // Defined ONCE and applied where `teams` is loaded, so no later reader has
  // to re-derive it — three copies of this rule is what hid the defect.
  const canRegisterAnyTeam = managesThisTournament || hasRole('admin', 'team_manager');

  // ── Tab + URL state ────────────────────────────────────────────────────────
  // #595 — the tab is in the URL (`?tab=`): a link, Back and a reload open it.
  // A value that is not a tab shows the overview. Each tab also checks its
  // own role and type below.
  const [tab, setTabTyped] = useUrlTab(TAB_IDS, 'overview');
  // The tab bar and the tabs pass an id as a string (TabBar types it so).
  const setTab = (next: string) => setTabTyped(next as Tab);

  // #552 — the tab bar scrolls sideways on a phone. Put the active tab in the
  // middle of the bar when the bar mounts, when the tab changes, and when a
  // tab comes or goes (the first bracket, the bout lock). A callback ref runs
  // only then: a socket update does not move a bar that the user scrolled.
  const tabSetKey = [brackets.length > 0, managesThisTournament, tournament?.type, tournament?.is_locked].join('|');
  const tabsBarRef = useCallback((bar: HTMLDivElement | null) => {
    const el = bar?.querySelector<HTMLElement>('[aria-current="true"]');
    if (!bar || !el) return;
    const barBox = bar.getBoundingClientRect();
    const elBox = el.getBoundingClientRect();
    bar.scrollLeft += elBox.left - barBox.left - (barBox.width - elBox.width) / 2;
  }, [tab, tabSetKey]);

  // QR code (print-only, client-side generated)
  const [qrDataUrl, setQrDataUrl] = useState<string>('');
  useEffect(() => {
    if (!id) return;
    const url = `${window.location.origin}/tournaments/${id}`;
    QRCode.toDataURL(url, { width: 80, margin: 0 }).then(setQrDataUrl).catch(logBackgroundError('TournamentDetail:qr-code'));
  }, [id]);

  // ── Local UI state (shell-level modals) ────────────────────────────────────
  const [showEdit, setShowEdit] = useState(false);
  const [showBulkReg, setShowBulkReg] = useState(false);
  const [showGuestRegPrompt, setShowGuestRegPrompt] = useState(false); // #263
  // Register Boxers: the teams of the person (see canRegisterAnyTeam above).
  const { data: teams } = useLoad(
    canManageAthletes
      ? () => getTeams().then(r => (canRegisterAnyTeam ? r.data : r.data.filter(c => c.my_role)) as Team[])
      : null,
    [canRegisterAnyTeam],
    {
      context: 'TournamentDetail:teams',
      initial: [] as Team[],
      // `showError` records the failure as `ui_error_shown`: no second exception.
      expected: () => true,
      onError: err => showError(err),
    },
  );
  const logoInputRef = useRef<HTMLInputElement>(null);
  // #605 — one write at a time for each action. The lock switches of the
  // header and the unlock of the Check-in tab share one busy lock. Thus only one
  // lock request runs at a time, and an older answer cannot come last.
  const lockBusy = useBusy();
  const startingDay = useBusy();
  // #664 — ONE lock for the upload and the removal of the logo: two logo writes
  // at one time can end in either order. The delete has its own lock.
  const logoWrite = useBusy();
  const deleting = useBusy();

  // Bracket preview (shared between Registrations + Brackets tabs)
  const [bracketPreview, setBracketPreview] = useState<BracketPreview | null>(null);
  const previewDraw = useBusy();

  // Bout plan / pivot (shared between Brackets tab and modals)
  const [boutPlan, setBoutPlan] = useState<BoutPlanView | null>(null);
  const [showBoutPlan, setShowBoutPlan] = useState(false);
  const [showPivotAnalysis, setShowPivotAnalysis] = useState(false);

  // Manual bout assignment
  const [showManualAssign, setShowManualAssign] = useState(false);
  const [manualBouts, setManualBouts] = useState<ManualBoutRow[] | null>(null);
  const [manualRings, setManualRings] = useState<Ring[]>([]);
  const [manualDays, setManualDays] = useState<string[]>([]);

  // Officials plan (opened from Officials or Bouts tab)
  const [showOfficialsPlan, setShowOfficialsPlan] = useState(false);
  // #588 — the officials that the bouts have now (read-only, Officials tab)
  const [showOfficialsView, setShowOfficialsView] = useState(false);

  // Open ring match preview
  const [openRingPairs, setOpenRingPairs] = useState<OpenRingPreviewData | null>(null);

  // All weight classes for CheckinTab
  const [allWeightClasses, setAllWeightClasses] = useState<WeightClass[]>([]);

  // Age groups for the bout form (read for the persons who manage athletes).
  const { data: ageGroups } = useLoad(
    canManageAthletes ? () => getAgeGroups().then(r => r.data) : null,
    [],
    { context: 'TournamentDetail:ageGroups', initial: [] as AgeGroup[] },
  );

  // ── Effects ────────────────────────────────────────────────────────────────

  // Load all weight classes for CheckinTab
  useEffect(() => {
    if (!managesThisTournament || !runsAsOpenRing(tournament?.type)) return;
    getWeightClasses({}).then(r => setAllWeightClasses(r.data)).catch(logBackgroundError('TournamentDetail:weight-classes'));
  }, [managesThisTournament, tournament?.type]);

  const tabTitleByTab: Record<Tab, string> = {
    overview:      t.tournament.overview,
    bouts:         t.tournament.bouts,
    brackets:      t.tournament.brackets,
    registrations: t.tournament.athletes,
    ratings:       t.tournament.teamRatings,
    checkin:       t.tournament.checkinTab,
    weighin:       t.tournament.weighIn,
    officials:     t.tournament.officials,
  };
  usePageTitle([tabTitleByTab[tab], tournament?.name]);

  // ── Derived ────────────────────────────────────────────────────────────────
  const showSkeleton = useDeferredLoading(loading);
  if (showSkeleton) return (
    <div className="space-y-4 fade-in">
      <Skeleton.Section lines={3} />
      <div className="flex gap-2">
        {Array.from({ length: 5 }).map((_, i) => <Skeleton.Bar key={i} className="h-7 w-20" />)}
      </div>
      <Skeleton.List shape="section" count={3} />
    </div>
  );
  if (loading) return null;
  if (!tournament) {
    // #457 — a failed load is not "not found": only a missing item says that (#661, F28).
    return (
      <div className="fade-in">
        <LoadFailure error={loadError} onRetry={retryLoad}
          notFound={{ icon: <TrophyIcon className="w-12 h-12" />, title: t.common.tournamentNotFound, to: '/tournaments', linkLabel: t.common.allTournaments }} />
      </div>
    );
  }

  const myRefereeEntry  = referees.find(r => r.id === user?.id);
  const isInRefereePool = !!myRefereeEntry;

  const currentBoutsByRing: Record<string, any> = {};
  bouts.filter(b => b.status === 'current').forEach(b => {
    if (b.ring_id) currentBoutsByRing[b.ring_id] = b;
  });
  const currentBoutsAll = bouts.filter(b => b.status === 'current');

  const tabs = [
    { id: 'overview',      label: t.tournament.overview },
    { id: 'bouts',         label: `${t.tournament.bouts} (${bouts.length})` },
    ...(brackets.length ? [{ id: 'brackets', label: `${t.tournament.brackets} (${brackets.length})` }] : []),
    { id: 'registrations', label: `${t.tournament.athletes} (${registrations.length})` },
    // #536 — the tabs follow the rules of the type: Team Ratings for a
    // championship part, Check-in for the open-ring rules, Weigh-in for the
    // championship rules. A hybrid has Team Ratings and Check-in.
    ...(hasChampionshipPart(tournament?.type) ? [{ id: 'ratings', label: t.tournament.teamRatings }] : []),
    ...(runsAsOpenRing(tournament?.type) && managesThisTournament ? [{ id: 'checkin', label: `${t.tournament.checkinTab} (${registrations.filter(r => weighinStatusOn(r, (tournament.date_start || '').slice(0, 10)) === 'ready').length}/${registrations.length})` }] : []),
    ...(!runsAsOpenRing(tournament?.type) && managesThisTournament && tournament.is_locked ? [{ id: 'weighin', label: t.tournament.weighIn }] : []),
    { id: 'officials',     label: `${t.tournament.officials} (${referees.length})` },
  ];

  async function toggleLock(field: 'is_locked' | 'coach_edits_locked' | 'registration_locked') {
    if (!tournament) return;
    const newVal = !tournament[field];
    // The question, and the label of its confirm button: the label of the switch (#665 review).
    const labels: Record<string, [[string, string], [string, string]]> = {
      is_locked: [[t.tournament.lockBoutsConfirm, t.tournament.lockBouts], [t.tournament.unlockBoutsConfirm, t.tournament.unlockBouts]],
      coach_edits_locked: [[t.tournament.lockCoachEditsConfirm, t.tournament.lockCoachEdits], [t.tournament.unlockCoachEditsConfirm, t.tournament.unlockCoachEdits]],
      registration_locked: [[t.tournament.lockRegistrationConfirm, t.tournament.lockRegistration], [t.tournament.unlockRegistrationConfirm, t.tournament.unlockRegistration]],
    };
    const [question, action] = newVal ? labels[field][0] : labels[field][1];
    // #605 — a second tap sends no second change. A refusal shows its
    // reason (CANNOT_UNLOCK_SCORED_BOUTS, COACH_EDITS_LOCKED_BY_BOUTS).
    // #665 — ask before run(): the question must not hold the lock.
    if (!(await confirm({ message: question, confirmLabel: action }))) return;
    await lockBusy.run(async () => {
      try {
        const updated = await updateTournament(id!, { [field]: newVal });
        patchTournament(updated.data);
      } catch (err) { showError(err); }
    });
  }

  async function handleDeleteTournament() {
    // #457 — a hard delete asks twice, as the account erasure does.
    // #665 — the second question starts with its button off (useConfirm).
    if (!(await confirm({ message: t.tournament.deleteConfirm1, confirmLabel: t.common.delete, destructive: true }))) return;
    if (!(await confirm({ message: t.tournament.deleteConfirm2, confirmLabel: t.common.delete, destructive: true }))) return;
    await deleting.run(async () => {
      try { await deleteTournament(id!); navigate('/tournaments'); }
      catch (err) { showError(err); }
    });
  }

  // Registration (#457). The server refuses a new registration under either
  // lock for everyone but an admin (403 REGISTRATION_LOCKED). The deadline is
  // information only. While coaches can register, the registration panel
  // below the header holds the Register Boxers button (operator, 2026-09-25).
  // "Disable coach corrections" closes the panel (#490): the coaches cannot
  // register any more, and the manager and the admin register walk-ins with
  // the small header button.
  const { all: allBoutsScored, any: anyBoutScored } = boutScoreState(bouts);
  const registrationOpen = !coachEditsLocked(tournament) && !allBoutsScored;
  const canRegister = teams.length > 0 && !allBoutsScored && (!registrationLocked(tournament) || hasRole('admin')) && (
    managesThisTournament || (canManageAthletes && !coachEditsLocked(tournament))
  );
  // #615 — the actions of the header go in one row under the header
  // content, so the name and the dates keep the full width.
  const nextStartDay = managesThisTournament && tournament.is_locked && bouts.some(b => b.status === 'pending') && !bouts.some(b => b.status === 'current')
    ? [...new Set(bouts.filter(b => b.bout_date && b.status === 'pending').map(b => String(b.bout_date).slice(0, 10)))].sort()[0]
    : undefined;
  // The manager's walk-in button after "Disable coach corrections" (#490),
  // and the admin's override under a lock (#457). While coaches can
  // register, the button is in the registration panel instead.
  const showRegisterButton = canRegister && !registrationOpen;
  const showCoachLockButton = managesThisTournament && !tournament.is_locked && !tournament.registration_locked && !anyBoutScored;
  const showRegistrationLockButton = managesThisTournament && !tournament.is_locked && !anyBoutScored;
  // "Lock bouts" while the list is unlocked, at any time: the server allows
  // it. "Unlock bouts" only before the first result: the server refuses it
  // after one (CANNOT_UNLOCK_SCORED_BOUTS). #536 — a hybrid scores its
  // bracket walk-overs at End Check-in, BEFORE its lock, and the whole
  // switch was hidden from that moment.
  const showBoutLockButton = managesThisTournament && (!tournament.is_locked || !anyBoutScored);
  const hasHeaderActions = !!nextStartDay || showRegisterButton || showCoachLockButton || showRegistrationLockButton || showBoutLockButton;
  // #615 — the state pills and the link pills of the header. With none of
  // them, the header draws no line under the text.
  const showStatePills = (managesThisTournament || canManageAthletes) && !!(tournament.is_locked || tournament.coach_edits_locked || tournament.registration_locked);
  const headerDeadline = registrationOpen ? null : tournament.registration_deadline;
  const hasInfoPills = !!(headerDeadline || tournament.facebook_url || tournament.livestream_url || tournament.contact_phone);

  return (
    <div className="space-y-4">
      <div className="print:hidden"><BackLink to="/tournaments" label={t.common.allTournaments} /></div>

      {/* Header */}
      <div className="card overflow-hidden">

        {/* Hidden file input */}
        <input ref={logoInputRef} type="file" accept="image/*" className="hidden"
          onChange={async e => {
            const file = e.target.files?.[0]; if (!file) return;
            try {
              await logoWrite.run(async () => {
                try {
                  await uploadTournamentLogo(id!, file);
                } catch (err) { showError(err); return; }
                // #419 — the logo is stored. A failed refetch is a warning, not an error.
                await afterWrite({ source: 'TournamentDetail:logo', refresh: reload });
              });
            } finally {
              e.target.value = '';
            }
          }} />

        {/* #615 — every logo is a banner, for every viewer. The manager has
            a camera button in the corner: visible also on a touch screen. */}
        {tournament.logo_url && (
          <TournamentBanner url={tournament.logo_url} maxHeight="max-h-56" className="no-print">
            {managesThisTournament && (
              <button onClick={() => logoInputRef.current?.click()} disabled={logoWrite.busy}
                title={t.tournament.changeLogo} aria-label={t.tournament.changeLogo}
                className="absolute right-3 bottom-3 w-10 h-10 rounded-full bg-black/50 hover:bg-black/70 flex items-center justify-center transition-colors disabled:opacity-50">
                {logoWrite.busy ? <span className="text-white">…</span> : <CameraIcon className="w-5 h-5 text-white" />}
              </button>
            )}
          </TournamentBanner>
        )}
        {/* #615 — no logo: the manager sees the place of the banner, with
            the upload button. Other viewers see no band. */}
        {!tournament.logo_url && managesThisTournament && (
          <button onClick={() => logoInputRef.current?.click()} disabled={logoWrite.busy}
            className="no-print w-full h-16 border-b border-dashed border-gray-200 bg-gray-50 hover:bg-gray-100 text-gray-500 text-sm inline-flex items-center justify-center gap-2 transition-colors disabled:opacity-50">
            <CameraIcon className="w-5 h-5" /> {logoWrite.busy ? '…' : t.tournament.addLogo}
          </button>
        )}

        <div className="p-5">
        <div className="flex flex-wrap items-start gap-3 justify-between">
          {/* #552 — on a phone the logo sits above the title, so the title,
              the description and the pills get the full width. */}
          {/* #615 — the title block takes the full width of the row, so the
              dates stand at its right edge, as on the card. */}
          <div className="flex-1 min-w-0 flex flex-col sm:flex-row items-start gap-3 sm:gap-4 print:contents">
            {/* Federation logo — print-only, first object in the title row.
                Sits to the left of the tournament name/details so the printed
                header reads as an LMF-branded document at a glance. The
                tournament-logo block below is no-print, so on print this is
                the only logo that renders. */}
            <img src="/logo.webp" alt="Lietuvos muaythai federacija"
              className="print-only w-16 h-16 object-contain shrink-0" style={{display:'none'}} />
            <div className="w-full sm:w-auto sm:flex-1 min-w-0 print:flex-1 print:text-center">
              {/* #615 — the name, the type and the age groups at the left, the
                  dates at the right: the layout of the tournament card. On
                  paper the dates go to the line of the location, so the name
                  gets the full width. */}
              <div className="flex items-start gap-3">
              <div className="flex-1 min-w-0 @container">
              {/* The pencil follows the last word of the title, also on a
                  title of two or three lines: the last word and the pencil
                  do not break apart. #615 — on paper the name is one line
                  (printNameSize). */}
              <h1 className={`text-xl sm:text-2xl font-black print:whitespace-nowrap print:overflow-hidden print:text-ellipsis ${printNameSize(tournament.name.length)}`}>
                {managesThisTournament ? (() => {
                  const cut = tournament.name.lastIndexOf(' ') + 1;
                  return (<>
                    {tournament.name.slice(0, cut)}
                    <span className="whitespace-nowrap">
                      {tournament.name.slice(cut)}
                      <button onClick={() => setShowEdit(true)} className="ml-1 p-1 -my-1 inline-flex align-middle text-gray-300 hover:text-gray-600 transition-colors no-print" title={t.tournament.editDetails} aria-label={t.tournament.editDetails}>
                        <PencilIcon className="w-4 h-4" />
                      </button>
                    </span>
                  </>);
                })() : tournament.name}
              </h1>
              <div className="flex mt-1 no-print"><TournamentTypeBadge type={tournament.type} where="header" /></div>
              <AgeGroupPills groups={tournament.allowed_age_groups} size="md" testId="tournament-age-groups-header" className="mt-1 print:justify-center" />
              </div>
              <TournamentDates start={tournament.date_start} end={tournament.date_end} testId="tournament-dates-header" className="print:hidden" />
              </div>
              {/* #615 — a line between the pills of the tournament and the
                  text. The lines do not print. */}
              <div className="border-t border-gray-100 my-3 print:hidden" />
              <div className="text-sm text-gray-500 flex items-center gap-2 print:justify-center">
                <span className="min-w-0">{tournament.location}</span>
                {tournament.country && <CountryDisplay code={tournament.country} size="sm" />}
                <span className="hidden print:inline">· {tournamentDatesLine(tournament.date_start, tournament.date_end)}</span>
              </div>
              {/* #532 — the full description, as plain text (React escapes
                  it), under the location, as on the card (operator,
                  2026-09-30). */}
              {tournament.description && (
                <p data-testid="tournament-description-header" className="text-sm text-gray-600 mt-2 whitespace-pre-line">{tournament.description}</p>
              )}
              {/* #615 — a line between the text and the state and link
                  pills, only when a pill shows. Every pill is 20 px tall:
                  `items-center`, so no pill stretches to the tallest of
                  the row. */}
              {(showStatePills || hasInfoPills) && (
              <>
              <div className="border-t border-gray-100 my-3 print:hidden" />
              <div className="flex flex-col gap-2 no-print">
              {showStatePills && (
              <div className="flex flex-wrap items-center gap-1.5">
                {tournament.is_locked && <span className="badge badge-gray inline-flex items-center gap-1"><LockIcon className="w-3 h-3" /> {t.tournament.boutsLocked}</span>}
                {tournament.coach_edits_locked && <span className="badge badge-amber inline-flex items-center gap-1"><LockIcon className="w-3 h-3" /> {t.tournament.coachEditsLocked}</span>}
                {tournament.registration_locked && <span className="badge badge-green inline-flex items-center gap-1"><CheckIcon className="w-3 h-3" /> {t.tournament.registrationComplete}</span>}
              </div>
              )}
              {/* While registration is open, the registration panel shows
                  the deadline. The header does not show it a second time. */}
              <TournamentInfoPills where="header"
                deadline={headerDeadline}
                facebookUrl={tournament.facebook_url} livestreamUrl={tournament.livestream_url}
                phone={tournament.contact_phone} />
              </div>
              </>
              )}
            </div>
          </div>
          {/* QR code — print only. Generated client-side so no external call
              and no data leak to third parties. The federation logo sits on
              the left of the title row above; the QR stays right-flushed so
              referees/spectators can scan from the printed page. */}
          {qrDataUrl && (
            <div className="print-only shrink-0 text-center" style={{display:'none'}}>
              <img src={qrDataUrl} alt={`QR code linking to ${tournament.name}`} className="w-16 h-16" />
              <div className="text-[8px] text-gray-400 mt-0.5">{window.location.host}</div>
            </div>
          )}
        </div>
        {/* #615 — the actions of the header: one row under the header
            content, with a line above it. */}
        {hasHeaderActions && (
          <div className="flex flex-wrap items-center gap-1.5 mt-4 pt-4 border-t border-gray-100 no-print">
            {nextStartDay && (
              <button
                // #605 — a second tap sends no second start.
                onClick={async () => {
                  const dayName = formatDay(nextStartDay, 'EEEE, dd MMM');
                  // #665 — ask before run(): the question must not hold the lock.
                  if (!(await confirm({ message: t.tournament.startDayConfirm.replace('{day}', dayName), confirmLabel: t.tournament.startDay }))) return;
                  await startingDay.run(async () => {
                  try {
                    const { startDay: sd } = await import('../api/client');
                    const res = await sd(id!, nextStartDay);
                    // #454 — a ring still live with another day's bout is
                    // skipped and named here; the other rings started.
                    // #605 — all the warnings go in one toast. A second
                    // warning toast replaces the first.
                    // #664 — a failed refetch keeps the advice in its warning.
                    await afterWrite({
                      source: 'TournamentDetail:start-day',
                      success: successText(res.data),
                      warnings: res.data.warnings,
                      refresh: reload,
                    });
                  } catch (err) { showError(err); }
                  });
                }}
                disabled={startingDay.busy}
                className="bg-green-600 text-white hover:bg-green-700 rounded-lg px-4 py-2 font-bold text-sm inline-flex items-center gap-2 shadow-md disabled:opacity-50">
                <PlayIcon className="w-4 h-4" />
                {t.tournament.startDay} {formatDay(nextStartDay, 'EEE dd MMM')}
              </button>
            )}
            {showRegisterButton && (
              <button onClick={() => setShowBulkReg(true)} data-testid="register-boxers-btn" className="btn-primary btn-sm">
                + {t.tournament.registerBoxers}
              </button>
            )}
            {showCoachLockButton && (
              <button onClick={() => toggleLock('coach_edits_locked')} disabled={lockBusy.busy} className="btn-outline btn-sm inline-flex items-center gap-1">
                {tournament.coach_edits_locked ? <LockOpenIcon className="w-3 h-3" /> : <LockIcon className="w-3 h-3" />}
                {tournament.coach_edits_locked ? t.tournament.unlockCoachEdits : t.tournament.lockCoachEdits}
              </button>
            )}
            {showRegistrationLockButton && (
              <button onClick={() => toggleLock('registration_locked')} disabled={lockBusy.busy} className="btn-outline btn-sm inline-flex items-center gap-1">
                {tournament.registration_locked ? <LockOpenIcon className="w-3 h-3" /> : <LockIcon className="w-3 h-3" />}
                {tournament.registration_locked ? t.tournament.unlockRegistration : t.tournament.lockRegistration}
              </button>
            )}
            {showBoutLockButton && (
              <button onClick={() => toggleLock('is_locked')} disabled={lockBusy.busy} className="btn-outline btn-sm inline-flex items-center gap-1">
                {tournament.is_locked ? <LockOpenIcon className="w-3 h-3" /> : <LockIcon className="w-3 h-3" />}
                {tournament.is_locked ? t.tournament.unlockBouts : t.tournament.lockBouts}
              </button>
            )}
          </div>
        )}
        </div>
      </div>


      {/* The registration panel (operator, 2026-09-25): the deadline, the
          number registered and the Register Boxers button, while registration
          is open. A guest gets the sign-in prompt (#263); a person who cannot
          register sees the facts only. */}
      {registrationOpen && (
        <RegistrationPanel
          deadline={tournament.registration_deadline ?? null}
          registered={registrations.length}
          onRegister={!user ? () => setShowGuestRegPrompt(true) : canRegister ? () => setShowBulkReg(true) : undefined}
        />
      )}

      {/* #263: guest register prompt — sign in, or read the coach guide */}
      {showGuestRegPrompt && (
        <GuestPrompt
          title={t.tournament.guestRegisterTitle}
          text={t.tournament.guestRegisterText}
          guideHref="/manual#coach"
          guideLabel={t.tournament.guestRegisterGuide}
          onClose={() => setShowGuestRegPrompt(false)}
        />
      )}

      {/* Register Boxers modal */}
      {showBulkReg && (
        <BulkRegisterModal
          tournamentId={id!}
          tournamentDate={tournament.date_start}
          allowedAgeGroups={tournament.allowed_age_groups ?? null}
          teams={teams}
          registrations={registrations}
          onClose={() => setShowBulkReg(false)}
          onDone={async () => { await reload(); }}
        />
      )}

      {/* Tabs */}
      <TabBar id="tournament-tabs" barRef={tabsBarRef} className="scroll-mt-16" tabs={tabs} activeTab={tab}
        onChange={next => {
          setTab(next);
          // Always scroll tabs into view — consistent behavior for all tabs
          setTimeout(() => {
            document.getElementById('tournament-tabs')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }, 50);
        }} />

      {/* ── Tab content ──────────────────────────────────────────────────────── */}

      {tab === 'overview' && (
        <OverviewTab
          id={id!} tournament={tournament} refreshTournament={refreshTournament}
          bouts={bouts} brackets={brackets} registrations={registrations}
          referees={referees} managers={managers} rings={rings}
          currentBoutsAll={currentBoutsAll} currentBoutsByRing={currentBoutsByRing}
          managesThisTournament={managesThisTournament}
          setTab={setTab}
          reload={reload}
        />
      )}

      {tab === 'registrations' && (
        <RegistrationsTab
          id={id!} tournament={tournament}
          registrations={registrations} patchRegistrations={patchRegistrations}
          brackets={brackets} teams={teams}
          managesThisTournament={managesThisTournament} canManageAthletes={canManageAthletes}
          tournamentDays={tournamentDays}
          setBracketPreview={setBracketPreview}
          previewDraw={previewDraw}
          setTab={setTab}
          reload={reload}
        />
      )}

      {tab === 'officials' && (
        <OfficialsTab
          id={id!} tournament={tournament}
          referees={referees}
          registrations={registrations}
          managesThisTournament={managesThisTournament}
          canOfficiate={canOfficiate} isInRefereePool={isInRefereePool}
          tournamentDays={tournamentDays} isMultiDay={isMultiDay}
          bouts={bouts}
          reload={reload}
          setShowOfficialsPlan={setShowOfficialsPlan}
          setShowOfficialsView={setShowOfficialsView}
        />
      )}

      {tab === 'bouts' && (
        <BoutsTab
          id={id!} tournament={tournament}
          bouts={bouts} patchBouts={patchBouts}
          brackets={brackets} registrations={registrations}
          referees={referees} juryAssignments={juryAssignments}
          rings={rings} tournamentDays={tournamentDays} isMultiDay={isMultiDay}
          managesThisTournament={managesThisTournament}
          canOfficiate={canOfficiate} isJury={isJury} isInRefereePool={isInRefereePool}
          boutSubs={boutSubs} setBoutSub={setBoutSub}
          ageGroups={ageGroups}
          setOpenRingPairs={setOpenRingPairs}
          setManualBouts={setManualBouts} setManualRings={setManualRings}
          setManualDays={setManualDays} setShowManualAssign={setShowManualAssign}
          setShowOfficialsPlan={setShowOfficialsPlan}
          reload={reload}
        />
      )}

      {tab === 'brackets' && (
        <BracketsTab key={id}
          id={id!} tournament={tournament}
          bouts={bouts} brackets={brackets} loadBrackets={loadBrackets} bracketsVersion={bracketsVersion}
          rings={rings} managesThisTournament={managesThisTournament}
          boutPlan={boutPlan} setBoutPlan={setBoutPlan}
          setShowBoutPlan={setShowBoutPlan} setShowPivotAnalysis={setShowPivotAnalysis}
          setManualBouts={setManualBouts} setManualRings={setManualRings}
          setManualDays={setManualDays} setShowManualAssign={setShowManualAssign}
          reload={reload}
        />
      )}

      {tab === 'checkin' && runsAsOpenRing(tournament?.type) && managesThisTournament && (
        <div className="card p-2 sm:p-4">
          <CheckinTab
            tournamentId={id!}
            tournamentDate={tournament.date_start}
            registrations={registrations}
            weightClasses={allWeightClasses}
            onRegistrationUpdated={reload}
            // #490 — the confirm refusal (403) follows these two locks, so the
            // tab turns Ready and Reset off under them. The header shows the
            // registration switch only under the same condition.
            lockedBy={tournament.is_locked ? 'bouts' : tournament.registration_locked ? 'registration' : null}
            onUnlockRegistration={tournament.registration_locked && !tournament.is_locked && !anyBoutScored
              ? () => toggleLock('registration_locked') : undefined}
            unlockBusy={lockBusy.busy}
          />
        </div>
      )}

      {tab === 'weighin' && !runsAsOpenRing(tournament?.type) && managesThisTournament && (
        <WeighinTab
          id={id!}
          tournament={tournament}
          tournamentDays={tournamentDays}
          weighins={weighins} setWeighins={setWeighins}
          weighinEndedDays={weighinEndedDays} setWeighinEnded={setWeighinEnded}
          weighinProposal={weighinProposal} setWeighinProposal={setWeighinProposal}

          reload={reload}
        />
      )}

      {tab === 'ratings' && hasChampionshipPart(tournament?.type) && (
        <TeamRatingsTab tournamentId={id!} onPrint={() => window.print()} bouts={bouts} />
      )}

      {/* ── Shared modals ────────────────────────────────────────────────────── */}

      {openRingPairs && (
        <OpenRingMatchPreviewModal
          pairs={openRingPairs} tournamentId={id!}
          onClose={() => setOpenRingPairs(null)}
          onConfirm={() => { setOpenRingPairs(null); return reload(); }}
        />
      )}

      {bracketPreview && (
        <BracketPreviewModal
          preview={bracketPreview} tournamentId={id!}
          onClose={() => setBracketPreview(null)}
          onConfirm={() => { setBracketPreview(null); return loadBrackets(); }}
          draw={previewDraw} onPreviewChange={setBracketPreview}
        />
      )}

      {showManualAssign && manualBouts && (
        <ManualBoutAssignModal
          bouts={manualBouts} rings={manualRings} days={manualDays} tournamentId={id!}
          onClose={() => setShowManualAssign(false)}
          // #664 — the lists come from the guarded readers of the hook, not
          // from the modal: a copy that the modal read earlier landed last.
          onSaved={() => {
            setShowManualAssign(false); setManualBouts(null);
            return Promise.all([reload(), loadBrackets()]);
          }}
          onBoutsChange={setManualBouts}
        />
      )}

      {showOfficialsPlan && (
        <PlanOfficialsModal
          rings={rings} referees={referees} tournamentId={id!}
          onClose={() => setShowOfficialsPlan(false)}
          onApplied={() => { setShowOfficialsPlan(false); return reload(); }}
        />
      )}

      {showOfficialsView && (
        <ViewOfficialsModal
          rings={rings} bouts={bouts} referees={referees} registrations={registrations} isMultiDay={isMultiDay}
          onClose={() => setShowOfficialsView(false)}
        />
      )}

      {showBoutPlan && boutPlan && (
        <BoutPlanModal
          plan={boutPlan} rings={rings} tournamentId={id!}
          readOnly={tournament?.bout_plan_status === 'confirmed'}
          onClose={() => setShowBoutPlan(false)}
          onConfirmed={() => {
            setShowBoutPlan(false); setBoutPlan(null);
            return Promise.all([reload(), loadBrackets()]);
          }}
          onShowAnalysis={() => setShowPivotAnalysis(true)}
          onPlanChange={setBoutPlan}
        />
      )}

      {showPivotAnalysis && boutPlan && (
        // #480 — the analysis reads the plan of the server, with its links
        // and its computed rests. The plan rows reached it without their
        // links, so it found no dependency and reported no issue.
        <BoutPivotAnalysis
          plan={boutPlan}
          rings={rings}
          onClose={() => setShowPivotAnalysis(false)}
        />
      )}

      {showEdit && tournament && (
        <TournamentFormModal
          tournament={tournament}
          onSave={async (data) => {
            await updateTournament(id!, data); setShowEdit(false);
            // #419 — the save committed. A failed refetch is a warning, not an error.
            // The modal is closed, so its error line cannot show it.
            await afterWrite({ source: 'TournamentDetail:edit', refresh: reload });
          }}
          onClose={() => setShowEdit(false)}
          onUploadLogo={() => { setShowEdit(false); setTimeout(() => logoInputRef.current?.click(), 100); }}
          // #605 — a second tap sends no second removal.
          onRemoveLogo={async () => {
            // #665 — ask before run(): the question must not hold the lock.
            if (!(await confirm({ message: t.tournament.removeLogoConfirm, confirmLabel: t.common.remove, destructive: true }))) return;
            await logoWrite.run(async () => {
              try {
                await deleteTournamentLogo(id!);
                patchTournament({ logo_url: undefined });
                setShowEdit(false);
              } catch (err) { showError(err); }
            });
          }}
          removeLogoDisabled={logoWrite.busy}
          onDelete={canDeleteTournament(tournament, bouts, hasRole('admin')) ? () => {
            setShowEdit(false); handleDeleteTournament();
          } : undefined}
        />
      )}
    </div>
  );
}
